import { useCallback, useState } from "react";
import { useInView } from "react-intersection-observer";

import { SIDEBAR_INFINITE_SCROLL_ROOT_MARGIN } from "../constants/infinite-scroll";

interface IUseSidebarInfiniteScrollResult {
  // NOTE: Attach to the sentinel that marks the end of the list.
  ref: (element: HTMLElement | null) => void;
  isInView: boolean;
}

// NOTE: Walks up to the nearest scrollable ancestor. Deliberately does not
// require it to be overflowing right now: it is the observer's root either way,
// and an empty list that later grows must not be stuck with the viewport.
const findScrollParent = (element: HTMLElement): HTMLElement | null => {
  let parentElement = element.parentElement;

  while (parentElement) {
    const { overflowY } = globalThis.getComputedStyle(parentElement);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parentElement;
    }
    parentElement = parentElement.parentElement;
  }

  return null;
};

/**
 * NOTE: NSAI-5822 — the sidebar's "fetch the next page" sentinel, observed
 * against the sidebar's own scrollport rather than the browser viewport.
 *
 * A plain `useInView()` watches the viewport, and `rootMargin` only ever
 * expands the ROOT's rectangle — never the rectangles of the scroll containers
 * clipping the target on the way up. So a sentinel sitting just past the bottom
 * of the sidebar's scrollport is clipped to zero area and never intersects, no
 * matter how generous the margin; the archived list only ever paginated when
 * the sentinel happened to land inside the scrollport by a pixel or two. Making
 * the scrollport the root is what lets the margin do its job, and the page is
 * then fetched a screenful before the user reaches the end of the list.
 */
export const useSidebarInfiniteScroll = (): IUseSidebarInfiniteScrollResult => {
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
  const { ref: inViewRef, inView: isInView } = useInView({
    root: scrollParent,
    rootMargin: SIDEBAR_INFINITE_SCROLL_ROOT_MARGIN,
  });

  const ref = useCallback(
    (element: HTMLElement | null): void => {
      inViewRef(element);
      setScrollParent(element ? findScrollParent(element) : null);
    },
    [inViewRef],
  );

  return { ref, isInView };
};
